/**
 * 管理者(承認者)によるパスワードの再設定(2026-09-02 発注者指示)。
 *
 * Cloud Functions(Admin SDK)が使えないため、createAuthUser と同じ方式で
 * 使い捨ての副次Firebaseアプリを立て、対象ユーザーとして入り直して変更する。
 * 管理者自身のログインは維持される(別アプリのセッションのため)。
 *
 * 前提: パスワードは全社で1つ。管理者は「いまのパスワード」を知っている。
 * ※Cloud Functions を導入できたら Admin SDK 経由に置き換える。
 */
import { deleteApp, initializeApp } from 'firebase/app';
import { deleteUser, getAuth, signInWithEmailAndPassword, signOut, updatePassword } from 'firebase/auth';
import { getFirebaseConfig } from '@core/lib/firebase';

export async function resetUserPassword(
  email: string,
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const secondary = initializeApp(getFirebaseConfig(), `pw-reset-${Date.now()}-${Math.random()}`);
  try {
    const auth = getAuth(secondary);
    const cred = await signInWithEmailAndPassword(auth, email, currentPassword);
    await updatePassword(cred.user, newPassword);
    await signOut(auth);
  } finally {
    await deleteApp(secondary);
  }
}

/**
 * ログイン用アカウント(Firebase Auth)の削除。2026-09-03 追加。
 *
 * users のドキュメントを消すだけでは Auth のアカウントが残り、
 * 同じ社内IDで作り直せなくなる(「既に使われています」)。
 * Admin SDK が無いので、本人として入り直してから自分を削除する。
 */
export async function deleteAuthUser(email: string, password: string): Promise<void> {
  const secondary = initializeApp(getFirebaseConfig(), `del-${Date.now()}-${Math.random()}`);
  try {
    const auth = getAuth(secondary);
    const cred = await signInWithEmailAndPassword(auth, email, password);
    await deleteUser(cred.user);
  } finally {
    await deleteApp(secondary);
  }
}

/** Firebase のエラーを日本語にする(画面に出すため) */
export function passwordErrorMessage(e: unknown): string {
  const code = (e as { code?: string })?.code ?? '';
  if (code.includes('wrong-password') || code.includes('invalid-credential')) {
    return 'いまのパスワードが違います(この人だけ別のパスワードになっている可能性があります)';
  }
  if (code.includes('weak-password')) return '新しいパスワードが簡単すぎます。8文字以上にしてください';
  if (code.includes('too-many-requests')) return '試行回数が多すぎます。しばらく待ってからお試しください';
  if (code.includes('user-not-found')) return 'このログインIDのアカウントが見つかりません';
  if (code.includes('user-disabled')) return 'このアカウントは停止されています';
  return e instanceof Error ? e.message : 'パスワードを変更できませんでした';
}
